import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, AtSign, Check, Phone, User } from 'lucide-react';
import { authAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import AuthShell from '../components/auth/AuthShell';
import {
    ErrorNote,
    Field,
    OtpBoxes,
    PrimaryButton,
    fa,
    isValidPhone,
    maskPhone,
    normalizePhone,
    useCountdown,
} from '../components/auth/authUi';

/**
 * ثبت‌نام مستقیم مشتری.
 *
 * دو قدم: اطلاعات ← کد تأیید. حساب فقط بعد از تأیید کد ساخته می‌شود (سمت
 * سرور)، پس اگر کاربر وسط کار صفحه را ببندد هیچ حساب نیمه‌کاره‌ای نمی‌ماند.
 * بعد از ثبت‌نام مستقیم وارد پنل می‌شود؛ تکمیل آدرس را گاردِ ProtectedRoute
 * خودش به صفحه‌ی پروفایل می‌فرستد.
 */

type Step = 'info' | 'code';

const RESEND_SECONDS = 90;

const Register: React.FC = () => {
    const navigate = useNavigate();
    const { login } = useAuth();

    const [step, setStep] = useState<Step>('info');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [alreadyMember, setAlreadyMember] = useState(false);
    const [touched, setTouched] = useState(false);

    const { left, restart } = useCountdown(RESEND_SECONDS);

    const normalized = normalizePhone(phone);
    const nameError = touched && firstName.trim().length < 2 ? 'نام را وارد کنید' : '';
    const phoneError = touched && !isValidPhone(normalized) ? 'شماره موبایل باید ۱۱ رقم و با ۰۹ شروع شود' : '';
    const emailError =
        touched && email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? 'ایمیل معتبر نیست' : '';

    const sendCode = async (e?: React.FormEvent) => {
        e?.preventDefault();
        setTouched(true);
        setError('');
        setAlreadyMember(false);

        if (firstName.trim().length < 2 || !isValidPhone(normalized) || emailError) return;

        setLoading(true);
        try {
            await authAPI.startRegistration(normalized);
            setStep('code');
            setCode('');
            restart();
        } catch (err: any) {
            const message = err?.message || 'ارسال کد ناموفق بود';
            // سرور برای شماره‌ی عضو ۴۰۹ می‌دهد؛ interceptor فقط پیام را نگه می‌دارد
            if (/قبلاً ثبت شده/.test(message)) setAlreadyMember(true);
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    const verify = async (value = code) => {
        if (value.length !== 6) return;
        setError('');
        setLoading(true);

        try {
            const res = await authAPI.completeRegistration({
                phone_number: normalized,
                code: value,
                first_name: firstName.trim(),
                last_name: lastName.trim() || undefined,
                email: email.trim() || undefined,
            });

            login(res.token, res.buyer);
            navigate('/dashboard', { replace: true });
        } catch (err: any) {
            setError(err?.message || 'کد تأیید نادرست است');
            setCode('');
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthShell
            eyebrow={step === 'info' ? 'قدم ۱ از ۲' : 'قدم ۲ از ۲'}
            title={step === 'info' ? 'ساخت حساب مشتری' : 'کد تأیید را وارد کنید'}
            subtitle={
                step === 'info'
                    ? 'چند ثانیه طول می‌کشد. بعد از ثبت‌نام می‌توانید مستقیم لایسنس بخرید.'
                    : `کد ۶ رقمی به ${maskPhone(normalized)} پیامک شد.`
            }
            footer={
                <>
                    قبلاً ثبت‌نام کرده‌اید؟{' '}
                    <Link to="/login" className="font-semibold text-emerald-700 hover:text-emerald-900">
                        ورود
                    </Link>
                </>
            }
        >
            {/* نشانگر پیشرفت */}
            <div className="mb-8 flex items-center gap-2" aria-hidden>
                <span className="h-1.5 flex-1 rounded-full bg-emerald-600" />
                <motion.span
                    className="h-1.5 flex-1 rounded-full"
                    animate={{ backgroundColor: step === 'code' ? '#059669' : '#E7E5E4' }}
                />
            </div>

            <AnimatePresence mode="wait">
                {step === 'info' ? (
                    <motion.form
                        key="info"
                        onSubmit={sendCode}
                        noValidate
                        initial={{ opacity: 0, x: -16 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 16 }}
                        transition={{ duration: 0.25 }}
                        className="space-y-5"
                    >
                        <div className="grid gap-4 sm:grid-cols-2">
                            <Field
                                label="نام"
                                icon={<User className="h-4 w-4" />}
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                autoComplete="given-name"
                                error={nameError}
                                autoFocus
                            />
                            <Field
                                label="نام خانوادگی"
                                optional
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                autoComplete="family-name"
                            />
                        </div>

                        <Field
                            label="شماره موبایل"
                            icon={<Phone className="h-4 w-4" />}
                            type="tel"
                            inputMode="numeric"
                            dir="ltr"
                            placeholder="09xx xxx xxxx"
                            className="text-left tracking-wider"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            autoComplete="tel"
                            error={phoneError}
                            hint="کد تأیید به همین شماره ارسال می‌شود"
                        />

                        <Field
                            label="ایمیل"
                            optional
                            icon={<AtSign className="h-4 w-4" />}
                            type="email"
                            dir="ltr"
                            className="text-left"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoComplete="email"
                            error={emailError}
                        />

                        {error && (
                            <ErrorNote>
                                {error}
                                {alreadyMember && (
                                    <Link to="/login" className="mr-2 font-semibold underline">
                                        رفتن به ورود
                                    </Link>
                                )}
                            </ErrorNote>
                        )}

                        <PrimaryButton type="submit" loading={loading}>
                            ارسال کد تأیید
                        </PrimaryButton>

                        <p className="text-center text-xs leading-6 text-stone-400">
                            با ثبت‌نام، شرایط استفاده از خدمات لایسنس اپ را می‌پذیرید.
                        </p>
                    </motion.form>
                ) : (
                    <motion.div
                        key="code"
                        initial={{ opacity: 0, x: -16 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 16 }}
                        transition={{ duration: 0.25 }}
                        className="space-y-6"
                    >
                        <OtpBoxes
                            value={code}
                            onChange={(v) => {
                                setCode(v);
                                if (error) setError('');
                            }}
                            onComplete={(v) => verify(v)}
                            disabled={loading}
                            invalid={Boolean(error)}
                        />

                        {error && <ErrorNote>{error}</ErrorNote>}

                        <PrimaryButton onClick={() => verify()} loading={loading} disabled={code.length !== 6}>
                            <span className="inline-flex items-center gap-2">
                                <Check className="h-4 w-4" />
                                تأیید و ساخت حساب
                            </span>
                        </PrimaryButton>

                        <div className="flex items-center justify-between text-sm">
                            <button
                                type="button"
                                onClick={() => {
                                    setStep('info');
                                    setError('');
                                }}
                                className="inline-flex items-center gap-1 text-stone-500 hover:text-stone-800"
                            >
                                <ArrowRight className="h-4 w-4" />
                                ویرایش اطلاعات
                            </button>

                            {left > 0 ? (
                                <span className="tabular-nums text-stone-400">
                                    ارسال دوباره تا {fa(Math.floor(left / 60))}:{fa(left % 60).padStart(2, '۰')}
                                </span>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => sendCode()}
                                    disabled={loading}
                                    className="font-semibold text-emerald-700 hover:text-emerald-900"
                                >
                                    ارسال دوباره‌ی کد
                                </button>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </AuthShell>
    );
};

export default Register;
